import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    Autocomplete,
    Grid,
    Chip,
    Collapse,
    Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { updateTournament } from '../../firebase/firestore';

const INK_COLORS = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];

const EditTournamentResults = ({ tournament, onResultsUpdated }) => {
    const [results, setResults] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (tournament?.results) {
            setResults(tournament.results);
        }
    }, [tournament]);

    const handleAddResult = () => {
        const newPlace = Object.keys(results).length + 1;
        const newResults = {
            ...results,
            [newPlace]: {
                player_name: '',
                score: '',
                points: 0,
                ink_colors: []
            }
        };
        setResults(newResults);
        handleInputChange(newResults);
    };

    const handleResultChange = (place, field, value) => {
        const newResults = {
            ...results,
            [place]: {
                ...results[place],
                [field]: field === 'points' ? parseInt(value) || 0 : value
            }
        };
        setResults(newResults);
        handleInputChange(newResults);
    };

    const handleInkColorsChange = (place, newValue) => {
        const newResults = {
            ...results,
            [place]: {
                ...results[place],
                ink_colors: newValue
            }
        };
        setResults(newResults);
        handleInputChange(newResults);
    };

    const handleDeleteResult = (place) => {
        const newResults = { ...results };
        delete newResults[place];

        // Przenumeruj pozostałe miejsca
        const reorderedResults = {};
        Object.values(newResults)
            .sort((a, b) => (a.points > b.points ? -1 : 1))
            .forEach((result, index) => {
                reorderedResults[index + 1] = result;
            });

        setResults(reorderedResults);
        handleInputChange(reorderedResults);
    };

    const handleInputChange = (newResults) => {
        if (onResultsUpdated) {
            onResultsUpdated({
                target: {
                    name: 'results',
                    value: newResults
                }
            });
        }
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            await updateTournament(tournament.id, { results });
            if (onResultsUpdated) {
                onResultsUpdated({
                    target: {
                        name: 'results',
                        value: results
                    }
                });
            }
        } catch (error) {
            console.error('Błąd podczas aktualizacji wyników:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    bgcolor: 'rgba(76, 29, 149, 0.1)',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    '&:hover': {
                        bgcolor: 'rgba(76, 29, 149, 0.15)',
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: 'rgb(196, 181, 253)', fontWeight: 500 }}>
                        {Object.keys(results).length > 0 ? `${Object.keys(results).length} wyników` : 'Brak wyników'}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    sx={{ color: 'rgb(196, 181, 253)' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(!expanded);
                    }}
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Stack spacing={1} sx={{ mt: 1 }}>
                    {Object.entries(results).map(([place, result]) => (
                        <Paper
                            key={place}
                            sx={{
                                p: 1.5,
                                bgcolor: 'rgba(0, 0, 0, 0.2)',
                                border: '1px solid rgba(76, 29, 149, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <Typography sx={{
                                color: 'rgb(196, 181, 253)',
                                minWidth: '24px',
                                textAlign: 'center',
                                fontWeight: 500
                            }}>
                                {place}
                            </Typography>

                            <TextField
                                size="small"
                                label="Gracz"
                                value={result.player_name || ''}
                                onChange={(e) => handleResultChange(place, 'player_name', e.target.value)}
                                sx={{
                                    flex: 2,
                                    minWidth: '120px',
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.7)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgb(196, 181, 253)',
                                    },
                                }}
                            />

                            <TextField
                                size="small"
                                label="Wynik"
                                value={result.score || ''}
                                onChange={(e) => handleResultChange(place, 'score', e.target.value)}
                                placeholder="np. 4-0-2"
                                sx={{
                                    flex: 1,
                                    minWidth: '80px',
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.7)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgb(196, 181, 253)',
                                    },
                                }}
                            />

                            <TextField
                                size="small"
                                label="Punkty"
                                type="number"
                                value={result.points || 0}
                                onChange={(e) => handleResultChange(place, 'points', e.target.value)}
                                sx={{
                                    flex: 1,
                                    minWidth: '80px',
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.7)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgb(196, 181, 253)',
                                    },
                                }}
                            />

                            <Autocomplete
                                multiple
                                size="small"
                                options={INK_COLORS}
                                value={result.ink_colors || []}
                                onChange={(e, newValue) => handleInkColorsChange(place, newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Kolory"
                                        size="small"
                                        sx={{
                                            flex: 2,
                                            minWidth: '150px',
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(76, 29, 149, 0.5)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(76, 29, 149, 0.7)',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgb(196, 181, 253)',
                                            },
                                        }}
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option}
                                            label={option}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                color: 'white',
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'rgb(196, 181, 253)',
                                                },
                                            }}
                                        />
                                    ))
                                }
                                sx={{
                                    '& .MuiAutocomplete-tag': {
                                        backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                        color: 'white',
                                    }
                                }}
                            />

                            <IconButton
                                size="small"
                                onClick={() => handleDeleteResult(place)}
                                sx={{ color: '#ef4444' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Paper>
                    ))}

                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleAddResult}
                            sx={{
                                color: 'rgb(196, 181, 253)',
                                borderColor: 'rgba(76, 29, 149, 0.5)',
                                '&:hover': {
                                    borderColor: 'rgb(196, 181, 253)',
                                    backgroundColor: 'rgba(76, 29, 149, 0.1)',
                                },
                            }}
                        >
                            Dodaj wynik
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{
                                backgroundColor: 'rgb(76, 29, 149)',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgb(91, 33, 182)',
                                },
                            }}
                        >
                            Zapisz wyniki
                        </Button>
                    </Box>
                </Stack>
            </Collapse>
        </Box>
    );
};

export default EditTournamentResults; 